import {updateObject} from '@/store/utils'
import {ModuleFc} from '@/project'
import {cloneDeep, sumBy} from 'lodash'
import {IssueState, Status, IssuePayload, GetInvoiceDocumentRequest} from './types'
import {validatePurchaseDate} from '@/pages/issue/utils/date'
import {EventProperty} from '../gtm/types'

export {IssueState}

const initState: IssueState = {
  writeDate: '',
  clientId: '',
  items: [],
  extraData: {
    cash: 0,
    checkBill: 0,
    note: 0,
    credit: 0,
    remark: '',
    purposeType: '청구',
  },
  previewData: '',
  copyInvoiceId: '',
}

const module: ModuleFc<IssueState> = (ctx) => ({
  namespaced: true,
  state: cloneDeep(initState),
  getters: {
    totalAmount(state) {
      return sumBy(state.items, 'totalAmount')
    },
    confirmWriteDateStatus(state): Status {
      return state.writeDate ? 'success' : 'danger'
    },
    confirmBizStatus(state, getters, rootState) {
      const {
        name, ownerName, bizRegistrationNumber,
        address, bizClass, bizType, taxRegistrationNumber, email
      } = rootState.biz
      if(!name || !ownerName || !bizRegistrationNumber) {
        return 'danger'
      }
      if(!address || !bizClass || !bizType || !taxRegistrationNumber || !email) {
        return 'warning'
      }
      return 'success'
    },
    confirmClientStatus(state, getters, rootState): Status {
      const {
        name, ownerName, bizRegistrationNumber,
        fullAddress, businessClass, businessType,
        taxRegistrationNumber
      } = rootState.clients.client
      if(!state.clientId || !name || !ownerName || !bizRegistrationNumber) {
        return 'danger'
      }
      if(!fullAddress || !businessClass || !businessType || !taxRegistrationNumber) {
        return 'warning'
      }
      return 'success'
    },
    confirmClientContactStatus(state, getters, rootState): Status {
      const {email, emailSub, phoneNumber} = rootState.clients.client
      if(!state.clientId) {
        return 'danger'
      }
      if(!email && !emailSub && !phoneNumber) {
        return 'warning'
      }
      return 'success'
    },
    confirmItemsStatus(state): Status {
      const states = state.items.map((item) => {
        const {itemName, qty, purchaseDate} = item
        const {writeDate} = state
        const validatePurchaseDateError = validatePurchaseDate(writeDate, purchaseDate)
        if(!itemName || !qty || validatePurchaseDateError) {
          return 'danger'
        }
        return 'success'
      })
      if(!states.length) {
        return 'danger'
      }
      return states.some((state) => state === 'danger') ? 'danger' : 'success'
    },
    isTaxFree(state): boolean {
      return state.items.some((item) => item.taxType === '면세')
    },
    gtmCommonInfo(state, getters): EventProperty {
      return {
        'invoice_type': getters.isTaxFree ? 'invoice' : 'tax_invoice',
        'item_quantity': state.items.length,
        'total_amount': getters.totalAmount,
        'workflow': 'issue_invoice',
      }
    }
  },
  mutations: {
    update(state, payload) {
      updateObject(state, payload)
    },
    clear(state) {
      updateObject(state, initState)
    },
    remove(state, payload: string) {
      state[payload] = cloneDeep(initState[payload])
    },
  },
  actions: {
    async getPreview({rootState}, payload: GetInvoiceDocumentRequest) {
      const {data} = await ctx.$axios({
        url: '/invoice-preview',
        params: {
          bizId: rootState.biz.id,
          type: payload.type,
        },
        data: payload,
        method: 'post',
      })
      return data
    },
    async issueInvoice({rootState}, payload: IssuePayload) {
      const {data} = await ctx.$axios({
        url: '/invoice',
        params: {
          bizId: rootState.biz.id,
        },
        data: payload,
        method: 'post',
      })
      return data
    },
  }
})

export default module
